import { formatUserFacingError, withErrorCode } from '@/lib/errors'
import { useEffect, useRef, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { MagicSelectTrigger as SelectTrigger } from '@/components/ui/magic-input'
import { Separator } from '@/components/ui/separator'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectValue,
} from '@/components/ui/select'
import { Paperclip, Trash2, AlertCircle } from 'lucide-react'
import { providerInvoicesService } from '../services/providerInvoices.service'
import type {
  ProviderInvoiceDocument,
  ProviderInvoiceLineItem,
  ProviderInvoiceLineType,
} from '@/types'

interface LineItemDraft {
  type: ProviderInvoiceLineType
  description: string
  amount: string
  receiptUrl?: string
  receiptName?: string
  uploading?: boolean
  uploadError?: string
}

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  memberId: string
  providerMemberId: string
  engagement: { id: string; title: string; clientName: string }
  invoice?: ProviderInvoiceDocument & { id: string }
  onSubmitted: () => void
}

function todayYmd(): string {
  const d = new Date()
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function emptyLine(): LineItemDraft {
  return { type: 'labor', description: '', amount: '' }
}

function fmtCurrency(n: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n)
}

export function SubmitInvoiceDialog({
  open,
  onOpenChange,
  memberId,
  providerMemberId,
  engagement,
  invoice,
  onSubmitted,
}: Props) {
  const isEdit = !!invoice
  const [invoiceDate, setInvoiceDate] = useState<string>(todayYmd())
  const [notes, setNotes] = useState<string>('')
  const [lines, setLines] = useState<LineItemDraft[]>([emptyLine()])
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const fileInputRefs = useRef<Record<number, HTMLInputElement | null>>({})

  useEffect(() => {
    if (!open) return
    if (invoice) {
      setInvoiceDate(invoice.invoiceDate)
      setNotes(invoice.notes ?? '')
      setLines(
        invoice.lineItems.length === 0
          ? [emptyLine()]
          : invoice.lineItems.map((li) => ({
              type: li.type,
              description: li.description,
              amount: String(li.amount),
              receiptUrl: li.receiptUrl,
              receiptName: li.receiptUrl ? 'Existing receipt' : undefined,
            })),
      )
    } else {
      setInvoiceDate(todayYmd())
      setNotes('')
      setLines([emptyLine()])
    }
    setSubmitError(null)
  }, [open, invoice])

  const subtotal = lines.reduce((acc, li) => acc + (Number(li.amount) || 0), 0)

  const anyUploading = lines.some((l) => l.uploading)
  const validLines =
    lines.length > 0 &&
    lines.every((li) => {
      const amt = Number(li.amount)
      if (!li.description.trim()) return false
      if (!(amt > 0)) return false
      if (li.type === 'expense' && !li.receiptUrl) return false
      return true
    })
  const canSubmit = !submitting && !anyUploading && validLines && !!invoiceDate

  function updateLine(i: number, patch: Partial<LineItemDraft>) {
    setLines((prev) => prev.map((li, idx) => (idx === i ? { ...li, ...patch } : li)))
  }

  function addLine() {
    setLines((prev) => [...prev, emptyLine()])
  }

  function removeLine(i: number) {
    setLines((prev) =>
      prev.length === 1 ? [emptyLine()] : prev.filter((_, idx) => idx !== i),
    )
  }

  async function handleReceiptPick(i: number, file: File) {
    updateLine(i, { uploading: true, uploadError: undefined, receiptName: file.name })
    try {
      const url = await providerInvoicesService.uploadReceipt(memberId, file)
      updateLine(i, { receiptUrl: url, uploading: false })
    } catch (e) {
      updateLine(i, {
        uploading: false,
        uploadError: formatUserFacingError(e, 'Upload failed'),
      })
    }
  }

  async function handleSubmit() {
    setSubmitting(true)
    setSubmitError(null)
    try {
      const lineItems: ProviderInvoiceLineItem[] = lines.map((li) => {
        const item: ProviderInvoiceLineItem = {
          type: li.type,
          description: li.description.trim(),
          amount: Number(li.amount),
        }
        if (li.receiptUrl) item.receiptUrl = li.receiptUrl
        return item
      })

      if (isEdit && invoice) {
        await providerInvoicesService.resubmit(invoice.id, {
          lineItems,
          invoiceDate,
          notes: notes.trim(),
        })
      } else {
        await providerInvoicesService.createSubmission({
          memberId,
          providerMemberId,
          assignmentId: engagement.id,
          engagementId: engagement.id,
          productionName: engagement.title,
          lineItems,
          invoiceDate,
          notes: notes.trim(),
        })
      }
      onSubmitted()
      onOpenChange(false)
    } catch (e) {
      setSubmitError(formatUserFacingError(e, 'Failed to submit invoice'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit & Resubmit Invoice' : 'Submit Invoice'}</DialogTitle>
        </DialogHeader>

        {isEdit && invoice?.rejectionNote && (
          <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm flex items-start gap-2">
            <AlertCircle className="h-4 w-4 mt-0.5 text-destructive shrink-0" />
            <div>
              <p className="font-medium text-destructive">Admin rejection note</p>
              <p className="text-muted-foreground mt-0.5">{invoice.rejectionNote}</p>
            </div>
          </div>
        )}

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-muted-foreground">Engagement</Label>
              <p className="font-medium text-sm mt-1">{engagement.title}</p>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Client</Label>
              <p className="font-medium text-sm mt-1">{engagement.clientName}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="invoiceDate">Invoice Date</Label>
              <Input
                id="invoiceDate"
                type="date"
                value={invoiceDate}
                onChange={(e) => setInvoiceDate(e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="notes">Notes (optional)</Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Anything the admin should know about this invoice…"
            />
          </div>

          <Separator />

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>Line Items</Label>
              <Button type="button" variant="outline" size="sm" onClick={addLine}>
                Add Line
              </Button>
            </div>

            {lines.map((li, i) => (
              <div key={i} className="rounded-md border p-3 space-y-3">
                <div className="grid grid-cols-12 gap-2 items-start">
                  <div className="col-span-3">
                    <Label className="text-xs text-muted-foreground">Type</Label>
                    <Select
                      value={li.type}
                      onValueChange={(v) =>
                        updateLine(i, {
                          type: v as ProviderInvoiceLineType,
                          ...(v === 'labor'
                            ? { receiptUrl: undefined, receiptName: undefined }
                            : {}),
                        })
                      }
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="labor">Labor</SelectItem>
                        <SelectItem value="expense">Expense</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-6">
                    <Label className="text-xs text-muted-foreground">Description</Label>
                    <Input
                      className="mt-1"
                      value={li.description}
                      onChange={(e) => updateLine(i, { description: e.target.value })}
                      placeholder={
                        li.type === 'labor' ? 'e.g. 4 hours instruction' : 'e.g. Studio rental'
                      }
                    />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs text-muted-foreground">Amount</Label>
                    <Input
                      className="mt-1"
                      type="number"
                      min="0.01"
                      step="0.01"
                      value={li.amount}
                      onChange={(e) => updateLine(i, { amount: e.target.value })}
                    />
                  </div>
                  <div className="col-span-1 flex justify-end pt-6">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeLine(i)}
                      aria-label="Remove line"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                {li.type === 'expense' && (
                  <div className="flex items-center gap-3 text-sm flex-wrap">
                    <input
                      ref={(el) => {
                        fileInputRefs.current[i] = el
                      }}
                      type="file"
                      accept="image/*,application/pdf"
                      hidden
                      onChange={(e) => {
                        const f = e.target.files?.[0]
                        if (f) void handleReceiptPick(i, f)
                        e.target.value = ''
                      }}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => fileInputRefs.current[i]?.click()}
                      disabled={li.uploading}
                    >
                      <Paperclip className="h-3.5 w-3.5 mr-1.5" />
                      {li.receiptUrl ? 'Replace Receipt' : 'Upload Receipt'}
                    </Button>
                    {li.uploading && <span className="text-muted-foreground">Uploading…</span>}
                    {li.uploadError && <span className="text-destructive">{li.uploadError}</span>}
                    {li.receiptUrl && !li.uploading && (
                      <a
                        href={li.receiptUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-muted-foreground underline truncate max-w-[200px]"
                      >
                        {li.receiptName ?? 'Receipt attached'}
                      </a>
                    )}
                    {!li.receiptUrl && !li.uploading && (
                      <span className="text-xs text-muted-foreground">
                        {withErrorCode('Receipt required.', 'app/validation')}
                      </span>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>

          <Separator />

          <div className="flex items-center justify-between">
            <Label className="text-sm">Subtotal</Label>
            <p className="text-lg font-semibold">{fmtCurrency(subtotal)}</p>
          </div>

          {submitError && <p className="text-sm text-destructive">{submitError}</p>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {submitting ? 'Submitting…' : isEdit ? 'Resubmit Invoice' : 'Submit Invoice'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
